<template>
<div>
    <p class="text-muted small">
        Guyana &amp; Trinidad Mutual — Health Insurance Claim Form, completed by
        doctor/dentist/optometrist/ophthalmologist/pharmacist.
    </p>

    <b-form @submit.prevent="onSubmit" novalidate>
        <h6>Employer / patient</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Employer"><b-form-input v-model="form.employer" /></b-form-group>
                <b-form-group label="Patient name"><b-form-input v-model="form.patient_name" /></b-form-group>
                <b-form-group label="Address"><b-form-textarea rows="2" v-model="form.address" /></b-form-group>
                <b-form-group label="Telephone"><b-form-input v-model="form.phone" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Date of birth"><b-form-datepicker v-model="form.dob" /></b-form-group>
                <b-form-group label="Insured's certificate No."><b-form-input v-model="form.cert_no" /></b-form-group>
                <b-form-group label="Doctor / provider name"><b-form-input v-model="form.provider_name" /></b-form-group>
                <b-form-group label="Identification # (compulsory)"><b-form-input v-model="form.identification" /></b-form-group>
            </b-col>
        </b-row>

        <h6>Services rendered</h6>
        <p class="small text-muted">Up to 8 lines. Diagnosis in BOLD LETTERS.</p>
        <b-table-simple small responsive>
            <b-thead><b-tr>
                <b-th>Date visited</b-th><b-th>Diagnosis</b-th><b-th>Services rendered</b-th><b-th>Charges</b-th><b-th>Co-payment</b-th>
            </b-tr></b-thead>
            <b-tbody>
                <b-tr v-for="(row, i) in form.services" :key="i">
                    <b-td><b-form-datepicker v-model="row.date" /></b-td>
                    <b-td><b-form-input v-model="row.diagnosis" /></b-td>
                    <b-td><b-form-input v-model="row.services" /></b-td>
                    <b-td><b-form-input v-model="row.charges" /></b-td>
                    <b-td><b-form-input v-model="row.copay" /></b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <h6>History</h6>
        <b-row>
            <b-col md="6"><b-form-group label="When did symptoms first appear / accident occur?"><b-form-input v-model="form.symptoms_onset" /></b-form-group></b-col>
            <b-col md="3"><b-form-group label="Arose out of employment?"><b-form-radio-group v-model="form.is_employment" :options="yesNoOpts" /></b-form-group></b-col>
            <b-col md="3"><b-form-group label="If yes, when?"><b-form-input v-model="form.employment_when" /></b-form-group></b-col>
        </b-row>
        <b-row>
            <b-col md="3"><b-form-group label="Other insurance plan?"><b-form-radio-group v-model="form.has_other_plan" :options="yesNoOpts" /></b-form-group></b-col>
            <b-col md="4"><b-form-group label="Insurance company name"><b-form-input v-model="form.other_company" /></b-form-group></b-col>
            <b-col md="5"><b-form-group label="Group plan insured under"><b-form-input v-model="form.other_group" /></b-form-group></b-col>
        </b-row>

        <div class="text-right mt-3">
            <b-button variant="secondary" @click="$emit('close')">Cancel</b-button>
            <b-button type="submit" variant="success">
                <i class="fa-solid fa-file-arrow-down"></i> Generate PDF
            </b-button>
        </div>
    </b-form>
</div>
</template>

<script>
import { formatDate, buildAddress, bestPhone, fullName, loadCoords } from "./pdfFiller"
import formPersistence from "./formPersistence"

export default {
    name: "gtm-health-form",
    mixins: [formPersistence],
    props: { demographics: { type: Object, default: () => ({}) } },
    data() {
        const blankRow = () => ({ date: "", diagnosis: "", services: "", charges: "", copay: "" })
        return {
            yesNoOpts: [{ value: "yes", text: "Yes" }, { value: "no", text: "No" }],
            form: {
                employer: "",
                patient_name: "",
                address: "",
                phone: "",
                dob: "",
                cert_no: "",
                provider_name: "",
                identification: "",
                services: Array.from({ length: 8 }, blankRow),
                symptoms_onset: "",
                is_employment: "no",
                employment_when: "",
                has_other_plan: "no",
                other_company: "",
                other_group: "",
            },
        }
    },
    watch: {
        demographics: {
            immediate: true,
            handler(d) {
                if (!d) return
                if (!this.form.patient_name) this.form.patient_name = fullName(d)
                if (!this.form.address) this.form.address = buildAddress(d)
                if (!this.form.phone) this.form.phone = bestPhone(d)
                if (!this.form.dob) this.form.dob = d.date_of_birth || ""
                if (!this.form.employer && d.employer) this.form.employer = d.employer?.name || d.employer
                if (!this.form.identification && d.id_number) this.form.identification = d.id_number
                if (!this.form.provider_name && d.primary_physician?.name) {
                    this.form.provider_name = d.primary_physician.name
                }
            },
        },
    },
    methods: {
        buildCoords() {
            // Approximate coords for gtm-health-insurance-claim.pdf page 2.
            const rowsStartY = 530
            const rowHeight = 27
            const serviceCoords = {}
            this.form.services.forEach((_, i) => {
                const y = rowsStartY - i * rowHeight
                serviceCoords[`row_${i}_date`] = { page: 1, x: 75, y, size: 9 }
                serviceCoords[`row_${i}_diagnosis`] = { page: 1, x: 150, y, size: 9 }
                serviceCoords[`row_${i}_services`] = { page: 1, x: 300, y, size: 9 }
                serviceCoords[`row_${i}_charges`] = { page: 1, x: 440, y, size: 9 }
                serviceCoords[`row_${i}_copay`] = { page: 1, x: 510, y, size: 9 }
            })

            return {
                employer: { page: 1, x: 170, y: 670 },
                patient_name: { page: 1, x: 140, y: 645 },
                address: { page: 1, x: 110, y: 620 },
                phone: { page: 1, x: 130, y: 595 },
                dob: { page: 1, x: 410, y: 645 },
                cert_no: { page: 1, x: 440, y: 620 },
                provider_name: { page: 1, x: 440, y: 595 },
                identification: { page: 1, x: 460, y: 100 },
                symptoms_onset: { page: 1, x: 280, y: 280 },
                is_employment_yes: { page: 1, x: 260, y: 260, type: "check" },
                is_employment_no: { page: 1, x: 290, y: 260, type: "check" },
                employment_when: { page: 1, x: 410, y: 260 },
                has_other_plan_yes: { page: 1, x: 215, y: 240, type: "check" },
                has_other_plan_no: { page: 1, x: 245, y: 240, type: "check" },
                other_company: { page: 1, x: 45, y: 220 },
                other_group: { page: 1, x: 320, y: 220 },
                ...serviceCoords,
            }
        },
        buildValues() {
            const f = this.form
            const rowValues = {}
            f.services.forEach((r, i) => {
                rowValues[`row_${i}_date`] = formatDate(r.date)
                rowValues[`row_${i}_diagnosis`] = (r.diagnosis || "").toUpperCase()
                rowValues[`row_${i}_services`] = r.services
                rowValues[`row_${i}_charges`] = r.charges
                rowValues[`row_${i}_copay`] = r.copay
            })
            return {
                employer: f.employer,
                patient_name: f.patient_name,
                address: f.address,
                phone: f.phone,
                dob: formatDate(f.dob),
                cert_no: f.cert_no,
                provider_name: f.provider_name,
                identification: f.identification,
                symptoms_onset: f.symptoms_onset,
                is_employment_text: f.is_employment === "yes" ? "Yes" : f.is_employment === "no" ? "No" : "",
                is_employment_yes: f.is_employment === "yes",
                is_employment_no: f.is_employment === "no",
                employment_when: f.employment_when,
                has_other_plan_text: f.has_other_plan === "yes" ? "Yes" : f.has_other_plan === "no" ? "No" : "",
                has_other_plan_yes: f.has_other_plan === "yes",
                has_other_plan_no: f.has_other_plan === "no",
                other_company: f.other_company,
                other_group: f.other_group,
                ...rowValues,
            }
        },
        async onSubmit() {
            try {
                const coords = (await loadCoords("gtm-health-insurance-claim")) || this.buildCoords()
                await this.fillAndPersist({ form_key: "gtm-health", title: "GTM Health Insurance Claim" }, {
                    templateUrl: "/forms/gtm-health-insurance-claim.pdf",
                    outputName: `gtm-health-${this.form.patient_name || "patient"}.pdf`,
                    values: this.buildValues(),
                    coords,
                    bold: true,
                    xOffset: 4,
                    removeFormFields: true,
                })
                this.$emit("close")
            } catch (e) {
                this.$bvToast.toast(e.message || "Could not generate PDF", {
                    title: "PDF generation failed",
                    variant: "danger",
                })
            }
        },
    },
}
</script>
